import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "../../RoyalMahal/hooks";
import { between, createRng, leafPath, r2 } from "./shapes";

/*
 * A genda phool mala laid over the top of an arch (an event card, the venue's arch):
 * a thick strand of marigolds with mango leaves, a rose at the crown, and its two ends
 * hanging down as short strings that finish in a tassel and sway.
 *
 * It fills its parent (which must be positioned) and measures it, so the strand follows
 * the arch exactly at any width. `radius` is the arch's CSS border-radius; like CSS, it
 * is clamped to half the width. The flowers bloom in from the crown once it is in view.
 */

// The mala starts this far (in degrees) above the arch's springing line on each side.
const SPAN = 22;
const LEAF_GREENS = ["#2e6b2a", "#3e7d2c", "#4f8f32", "#5fa83a"];

const useBoxSize = (ref) => {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const w = Math.round(el.clientWidth);
      setSize((prev) => (prev && prev.w === w ? prev : { w }));
    };
    read();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read);
      return () => window.removeEventListener("resize", read);
    }
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
};

/** The positions of every flower, leaf and tail for an arch `w` wide. */
const drawMala = (w, radius, inset, seed) => {
  const rng = createRng(seed);
  const r = Math.min(radius, w / 2);
  const g = r - inset; // the strand's own radius
  const flat = Math.max(0, w - 2 * r);
  const a0 = Math.PI + (SPAN * Math.PI) / 180;
  const arc = g * (1.5 * Math.PI - a0);
  const total = 2 * arc + flat;

  // Point `d` along the strand from its left end; `a` is the outward normal.
  const at = (d) => {
    if (d <= arc) {
      const a = a0 + d / g;
      return { x: r + g * Math.cos(a), y: r + g * Math.sin(a), a };
    }
    if (d <= arc + flat) return { x: r + (d - arc), y: r - g, a: 1.5 * Math.PI };
    const a = 1.5 * Math.PI + (d - arc - flat) / g;
    return { x: w - r + g * Math.cos(a), y: r + g * Math.sin(a), a };
  };

  const size = Math.max(14, Math.min(22, r * 0.105));
  const count = Math.max(8, Math.round(total / (size * 0.56)));
  const step = total / (count - 1);
  const mid = (count - 1) / 2;

  const blooms = [];
  const leaves = [];
  for (let k = 0; k < count; k += 1) {
    const p = at(k * step);
    const j = Math.round(Math.abs(k - mid)); // flowers from the crown, so both sides match
    const nx = Math.cos(p.a);
    const ny = Math.sin(p.a);
    const wobble = between(rng, -0.14, 0.14) * size;
    const s = size * between(rng, 0.9, 1.08);
    const id = j > 0 && j % 9 === 0 ? "ut-rose" : Math.floor(j / 3) % 2 ? "ut-marigold-yellow" : "ut-marigold";
    blooms.push({ x: p.x + nx * wobble, y: p.y + ny * wobble, s, id, d: j / Math.max(1, mid) });

    // Mango leaves peek out on the outer edge, a few on the inner edge
    if (k % 2 === 0) {
      const deg = (p.a * 180) / Math.PI;
      [-1, 1].forEach((side) => {
        leaves.push({
          x: p.x + nx * size * 0.2,
          y: p.y + ny * size * 0.2,
          rot: deg + side * between(rng, 24, 48),
          len: size * between(rng, 0.95, 1.2),
          color: LEAF_GREENS[Math.floor(rng() * LEAF_GREENS.length)],
          d: j / Math.max(1, mid),
        });
      });
      if (k % 6 === 0) {
        leaves.push({
          x: p.x - nx * size * 0.25,
          y: p.y - ny * size * 0.25,
          rot: deg + 180 + between(rng, -20, 20),
          len: size * 0.8,
          color: LEAF_GREENS[1],
          d: j / Math.max(1, mid),
        });
      }
    }
  }

  // The ends of the mala hang down as short strings
  const tail = (p) => ({
    x: p.x,
    y: p.y,
    flowers: Array.from({ length: 4 }, (_, i) => ({
      y: size * 0.55 + i * size * 0.62,
      s: size * (0.86 - i * 0.05),
      id: i % 2 ? "ut-marigold-yellow" : "ut-marigold",
    })),
    tassel: size * 0.55 + 4 * size * 0.62,
  });

  return {
    size,
    height: r + size * 5,
    blooms,
    leaves,
    crown: at(total / 2),
    tails: [tail(at(0)), tail(at(total))],
  };
};

/** The tassel at the end of each string: a gold cap and a silk fringe in the card's colour. */
const Tassel = ({ y, size }) => {
  const k = size / 18;
  return (
    <g transform={`translate(0 ${r2(y)}) scale(${r2(k)})`}>
      <circle cy="0" r="2.6" fill="url(#ut-gold)" />
      <path d="M-5,9 C-5,3 -2.6,1.6 0,1.6 C2.6,1.6 5,3 5,9 Z" className="ut-garland__tassel" />
      <rect x="-6" y="8.4" width="12" height="2.6" rx="1.2" fill="url(#ut-gold-h)" />
      <g className="ut-garland__fringe" strokeWidth="1.1" strokeLinecap="round" fill="none">
        {[-4, -2, 0, 2, 4].map((x) => (
          <path key={x} d={`M${x},11 Q${x * 1.1},16 ${x * 1.35},21`} />
        ))}
      </g>
    </g>
  );
};

const ArchGarland = ({ radius = 170, inset = 1, seed = 11, className = "" }) => {
  const ref = useRef(null);
  const box = useBoxSize(ref);
  const inView = useInView(ref, { threshold: 0.15 });
  const art = useMemo(() => (box && box.w > 40 ? drawMala(box.w, radius, inset, seed) : null), [box, radius, inset, seed]);

  return (
    <div ref={ref} className={`ut-garland ${inView ? "is-in" : ""} ${className}`} aria-hidden="true">
      {art && (
        <svg width={box.w} height={r2(art.height)} viewBox={`0 0 ${box.w} ${r2(art.height)}`} overflow="visible">
          {art.leaves.map((l, i) => (
            <path
              key={i}
              className="ut-garland__leaf"
              d={leafPath(r2(l.len), 0.36)}
              fill={l.color}
              transform={`translate(${r2(l.x)} ${r2(l.y)}) rotate(${r2(l.rot)})`}
              style={{ "--d": r2(l.d) }}
            />
          ))}

          {art.tails.map((t, i) => (
            <g key={i} transform={`translate(${r2(t.x)} ${r2(t.y)})`}>
              <g className={`ut-garland__tail ut-garland__tail--${i ? "right" : "left"}`}>
                <line x1="0" y1="0" x2="0" y2={r2(t.tassel)} stroke="#c9962b" strokeWidth="1" />
                {t.flowers.map((f, k) => (
                  <use key={k} href={`#${f.id}`} x={r2(-f.s / 2)} y={r2(f.y - f.s / 2)} width={r2(f.s)} height={r2(f.s)} />
                ))}
                <Tassel y={t.tassel} size={art.size} />
              </g>
            </g>
          ))}

          {art.blooms.map((b, i) => (
            <g
              key={i}
              className="ut-garland__bloom"
              transform={`translate(${r2(b.x)} ${r2(b.y)})`}
              style={{ "--x": `${r2(b.x)}px`, "--y": `${r2(b.y)}px`, "--d": r2(b.d) }}
            >
              <use href={`#${b.id}`} x={r2(-b.s / 2)} y={r2(-b.s / 2)} width={r2(b.s)} height={r2(b.s)} />
            </g>
          ))}

          {/* A rose at the crown, with a pair of leaves */}
          <g transform={`translate(${r2(art.crown.x)} ${r2(art.crown.y)})`}>
            <g className="ut-garland__crown">
              <path d={leafPath(r2(art.size * 1.3), 0.38)} fill="#3e7d2c" transform="rotate(200)" />
              <path d={leafPath(r2(art.size * 1.3), 0.38)} fill="#4f8f32" transform="rotate(-20)" />
              <use href="#ut-rose" x={r2(-art.size * 0.8)} y={r2(-art.size * 0.8)} width={r2(art.size * 1.6)} height={r2(art.size * 1.6)} />
            </g>
          </g>
        </svg>
      )}
    </div>
  );
};

export default ArchGarland;
